import { Button, Select, Space, Table, Tabs, Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useStore, useUnit } from 'effector-react';
import {
  DownloadOutlined,
  TableOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import ButtonGroup from 'antd/es/button/button-group';
import dayjs from 'dayjs';
import ContainerInner from '../../components/ContentContainer/ContainerInner.jsx';
import {
  $pollsResults,
  BizPollResultsGate,
  changeUploadFilesModalEv,
  downloadResultsEv,
  downloadResultsFx,
  downloadYearPlanEv,
  downloadYearPlanFx,
  getResultsListFx,
  getResultsListPlanFx,
  resetBizPollsResultsEv,
} from '../../models/bizPollsModel/index.js';
import { openReportsModalEv } from '../../models/organizationsModel/index.js';
import ReportsModal from '../OrganizationsDashboard/ReportsModal.jsx';
import { $regionsSelect } from '../../models/globalModel/index.js';
import { regions_dictionary } from '../../dictionaries/regions_districts_dictionary.js';
import { $userInfo } from '../../models/authModel/index.js';
import UploadBizPollsFileModal from '../../components/BizPollsResults/UploadBizPollsFileModal.jsx';

export default function BizPollsResults() {
  const curUser = useUnit($userInfo);
  const download = useUnit(downloadResultsEv);
  const downloadPlan = useUnit(downloadYearPlanEv);
  const openResultsModal = useUnit(openReportsModalEv);
  const changeUploadFileModal = useUnit(changeUploadFilesModalEv);
  const listPending = useUnit(getResultsListFx.pending);
  const planListPending = useUnit(getResultsListPlanFx.pending);
  const resetList = useUnit(resetBizPollsResultsEv);
  const loading = listPending || planListPending;
  const [activeTab, setActiveTab] = useState('small');
  const regions = useStore($regionsSelect);
  const buttonLoading = useUnit(downloadResultsFx.pending);
  const planButtonLoading = useUnit(downloadYearPlanFx.pending);
  const [activeRegion, setActiveRegion] = useState(null);
  const [activeQuarter, setActiveQuarter] = useState(null);

  const results = useUnit($pollsResults);
  useEffect(() => {
    if (!activeQuarter) {
      resetList();
    }
  }, [activeQuarter]);

  const reportsModalType = useMemo(() => {
    return activeQuarter && activeQuarter.length > 4
      ? 'business_climate_report'
      : 'business_climate_report_year';
  }, [activeQuarter]);

  const regionsFilteredOpts = useMemo(() => {
    if (
      regions &&
      regions?.length > 0 &&
      ['mio', 'chamber', 'rpp_bc'].includes(curUser.role)
    ) {
      setActiveRegion(curUser.region_id);
      return regions.filter((i) => i.value === curUser.region_id);
    }
    return regions;
  }, [curUser, regions]);

  const showUpload = useMemo(
    () => ['survey_admin', 'admin'].includes(curUser?.role),
    [curUser]
  );

  const columns = [
    { title: 'Регион', dataIndex: 'region', key: 'region' },
    { title: 'Количество', dataIndex: 'value', key: 'count' },
  ];

  const items = [
    {
      key: 'small',
      label: 'Микро и малый бизнес',
      children: (
        <Table
          dataSource={results.filter((item) =>
            activeRegion
              ? item.region === regions_dictionary[activeRegion]
              : true
          )}
          columns={columns}
          loading={loading}
          pagination={false}
        />
      ),
    },
    {
      key: 'medium',
      label: 'Средний бизнес',
      children: (
        <Table
          dataSource={results.filter((item) =>
            activeRegion
              ? item.region === regions_dictionary[activeRegion]
              : true
          )}
          columns={columns}
          loading={loading}
          pagination={false}
        />
      ),
    },
    {
      key: 'plan',
      label: 'Общий план',
      children: (
        <>
          <ButtonGroup>
            <Button
              icon={<TableOutlined />}
              onClick={() => openResultsModal('business_climate_report_plan')}
            >
              Результаты выгрузки
            </Button>
            <Tooltip title={!activeQuarter ? 'Выберите период' : null}>
              <Button
                icon={<DownloadOutlined />}
                type="primary"
                style={{ boxShadow: 'none' }}
                onClick={() => downloadPlan()}
                loading={planButtonLoading}
                disabled={!activeQuarter}
              >
                Импорт в excel
              </Button>
            </Tooltip>
          </ButtonGroup>
          <Table
            style={{ marginTop: 20 }}
            dataSource={results.filter((item) =>
              activeRegion
                ? item.region === regions_dictionary[activeRegion]
                : true
            )}
            columns={[
              { title: 'Регион', dataIndex: 'region', key: 'region' },
              { title: 'План общий', dataIndex: 'plan', key: 'plan' },
              { title: 'Факт общий', dataIndex: 'value_all', key: 'fact_all' },
              {
                title: 'Факт целевой',
                dataIndex: 'fact',
                key: 'fact',
              },
              {
                title: '% от целевого',
                key: 'plan_percent',
                render: (item) =>
                  item.fact
                    ? `${
                        item.value !== 0
                          ? Math.round(
                              (item.fact / (item.plan === 0 ? 1 : item.plan)) *
                                100
                            )
                          : 100
                      }%`
                    : '',
              },
              {
                title: 'План постоянных',
                dataIndex: 'plan_regular',
                key: 'plan_regular',
                render: (plan_regular) =>
                  plan_regular && plan_regular > 0 ? plan_regular : '',
              },
              {
                title: 'Факт постоянных',
                dataIndex: 'value_priority',
                key: 'value_priority',
              },
              {
                title: '% постоянных',
                key: 'target_plan_percent',
                render: (item) =>
                  item.value_priority && item.plan_regular
                    ? `${
                        item.value_priority !== 0
                          ? Math.round(
                              (item.value_priority /
                                (item.plan_regular === 0
                                  ? 1
                                  : item.plan_regular)) *
                                100
                            )
                          : 100
                      }%`
                    : '',
              },
            ]}
            loading={loading}
            pagination={false}
          />
        </>
      ),
    },
  ];

  const quarterItems = useMemo(() => {
    const quarters = {
      2024: [],
    };
    const currentYear = dayjs().year();
    if (!quarters[currentYear]) {
      for (let i = currentYear; i >= 2024; i--) {
        if (i === currentYear) {
          quarters[i] = Array.from(
            { length: dayjs().quarter() },
            (v, i) => i + 1
          );
        } else {
          quarters[i] = [1, 2, 3, 4];
        }
      }
    } else {
      quarters[2024] = [
        ...Array(dayjs().quarter())
          .keys()
          .map((item) => item + 1),
      ];
    }

    const result = [];
    Object.keys(quarters).forEach((year) => {
      Object.values(quarters[year]).forEach((quarter) => {
        result.push({
          label:
            quarter === 4 ? `годовой ${year}` : `${quarter} квартал ${year}`,
          value: quarter === 4 ? year.toString() : `${year}_${quarter}`,
        });
      });
    });

    return result;
  }, []);

  return (
    <ContainerInner
      title="Результаты опросов бизнеса"
      extra={
        <div>
          <Space>
            <Select
              style={{
                width: '200px',
              }}
              options={regionsFilteredOpts}
              value={activeRegion}
              onChange={setActiveRegion}
              allowClear
              showSearch
              placeholder="Выберите регион"
            />
            <Select
              style={{
                width: '200px',
              }}
              options={quarterItems}
              value={activeQuarter}
              onChange={setActiveQuarter}
              allowClear
              showSearch
              placeholder="Выберите квартал"
            />
            <Tooltip
              title={activeQuarter ? null : 'Выберите период'}
              placement={'left'}
            >
              <ButtonGroup>
                <Button
                  icon={<TableOutlined />}
                  onClick={() => openResultsModal()}
                  disabled={!activeQuarter}
                >
                  Результаты выгрузки
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  type="primary"
                  style={{ boxShadow: 'none' }}
                  onClick={() => download()}
                  loading={buttonLoading}
                  disabled={!activeQuarter}
                >
                  Выгрузить
                </Button>
              </ButtonGroup>
            </Tooltip>
          </Space>
        </div>
      }
    >
      {showUpload && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Tooltip
            title={'Загрузить БИН постоянных респондентов'}
            placement={'bottom'}
          >
            <Button
              icon={<UploadOutlined />}
              onClick={() => changeUploadFileModal({ open: true, type: 'bin' })}
            >
              БИН
            </Button>
          </Tooltip>
        </div>
      )}
      <UploadBizPollsFileModal />
      <ReportsModal type={reportsModalType} />
      <BizPollResultsGate
        business_type={activeTab}
        region_id={activeRegion}
        activeQuarter={activeQuarter}
      />
      <Tabs
        activeKey={activeTab}
        items={items}
        onChange={(key) => setActiveTab(key)}
      />
    </ContainerInner>
  );
}
